<script lang="ts" setup>
    import { CallsToAction } from '~/data/CallsToAction'
    import { OffsetGroup } from '~/data/OffsetGroup'
    import { OffsetItem } from '~/data/OffsetItem'

    const { data: projects } = await useStatamicEntries().getEntries<Project>({
        collection: 'projects',
    })
    const selectedCategory = ref<string | null>(null)
    const selectedType = ref<string | null>(null)
    const selectedSortBy = ref<string | null>(null)

    const { data: projectPage, refresh } =
        await useStatamicEntries().getEntryBySlug<ProjectPage>({
            collection: 'pages',
            value: 'projects',
        })

    useStatamicPreview(refresh)

    useHead({
        title: () => projectPage.value?.title ?? 'Projects',
        bodyAttrs: {
            class: {
                'project-listing': true,
                'with-feature-image': computed(
                    () => projectPage.value?.feature_image != null,
                ),
            },
        },
    })

    const offsets: Ref<OffsetGroup[]> = ref([])

    watch(selectedCategory, () => {
        offsets.value = []
    })

    const getOffset = (index: number): OffsetGroup => {
        if (!offsets.value[index]) {
            offsets.value[index] = OffsetGroup.random(
                callsToAction.callToActionAtIndex(index) !== null,
            )
        }

        return offsets.value[index]
    }

    const filteredProjects = computed(() => {
        const filteredProjects = projects.value.filter((project) => {
            if (selectedCategory.value) {
                if (
                    !project.categories.some(
                        (category) => category.slug === selectedCategory.value,
                    )
                ) {
                    return false
                }
            }

            if (selectedType.value) {
                if (
                    !project.types.some(
                        (category) => category.slug === selectedType.value,
                    )
                ) {
                    return false
                }
            }

            return true
        })

        if (selectedSortBy.value) {
            filteredProjects.sort((a, b) => {
                if (selectedSortBy.value === 'Newer First') {
                    return (
                        new Date(b.date).getTime() - new Date(a.date).getTime()
                    )
                }

                return new Date(a.date).getTime() - new Date(b.date).getTime()
            })
        }

        return filteredProjects
    })

    const indexOfFirstCta = () => {
        const setting = projectPage.value?.first_cta_position.value

        if (setting == 'third') {
            return 2
        } else if (setting == 'middle') {
            return Math.floor(filteredProjects.value.length / 2)
        }

        return 0
    }

    const callsToAction = new CallsToAction(
        projectPage.value?.calls_to_action ?? [],
        filteredProjects.value.length,
        indexOfFirstCta(),
    )

    const clearFilters = () => {
        selectedCategory.value = null
        selectedType.value = null
    }

    const sortField = {
        type: 'select',
        display: 'Sort',
        handle: 'sort',
        width: 100,
        options: [
            { key: 'Newer First', value: 'Newer First' },
            { key: 'Older First', value: 'Older First' },
        ],
        placeholder: 'Sort',
        validate: [],
    } as Field
</script>

<template>
    <div class="projects-list-container block-gap-b">
        <PageHeader
            v-if="projectPage"
            :image="projectPage.feature_image"
            :content="projectPage.page_intro"
        >
            <h1>{{ projectPage.title }}</h1>
        </PageHeader>

        <RoomToText
            v-if="projectPage?.room_to_content"
            :content="projectPage.room_to_content"
            :toOffset="projectPage.room_to_to_offset"
            class="-mb-16"
        />

        <form
            class="layout-grid block-gap-t project-filters relative z-10 uppercase"
        >
            <div
                :class="
                    new OffsetGroup(new OffsetItem(2, 2), new OffsetItem(1, 1))
                        .classes
                "
            >
                <div class="flex flex-col justify-start sm:flex-row sm:gap-4">
                    <InputTermSelect
                        :fitContent="true"
                        :taxonomies="['categories']"
                        :showLabel="false"
                        v-model="selectedCategory"
                        placeholder="Category"
                        default="All Categories"
                        class="!m-0 !w-auto text-xl"
                        data-aos="fade-up"
                        data-aos-delay="50"
                    />

                    <InputTermSelect
                        :taxonomies="['types']"
                        :fitContent="true"
                        :showLabel="false"
                        v-model="selectedType"
                        placeholder="Project Type"
                        default="All Types"
                        class="!m-0 !w-auto text-xl"
                        data-aos="fade-up"
                        data-aos-delay="150"
                    />

                    <InputSelect
                        :field="sortField"
                        v-model="selectedSortBy"
                        :showLabel="false"
                        :fitContent="true"
                        class="!m-0 text-xl sm:!ml-auto"
                        data-aos="fade-up"
                        data-aos-delay="250"
                    />
                </div>
            </div>
        </form>

        <Transition>
            <div
                v-if="filteredProjects.length === 0"
                class="block-gap-b flex flex-col items-center px-12 py-48"
            >
                <p class="mb-4 font-bold uppercase transition-all">
                    No matching projects found.
                </p>

                <button
                    @click="clearFilters()"
                    class="relative px-4 py-2 uppercase before:absolute before:inset-0 before:border before:transition-all hover:before:-inset-1"
                >
                    Clear Filters
                </button>
            </div>
        </Transition>

        <Transition>
            <ul class="transition-all" v-if="filteredProjects.length > 0">
                <TransitionGroup name="list">
                    <li
                        v-for="(project, i) in filteredProjects"
                        :key="project.id"
                        class="layout-grid block-gap-y group"
                    >
                        <ProjectItem
                            :project="project"
                            class="mb-12 group-last:mb-0 md:mb-0"
                            :class="getOffset(i).classes"
                            data-aos="fade-up"
                            data-aos-offset="-100"
                        />

                        <div
                            class="block-gap-t md:mt-0"
                            :class="getOffset(i).complementary.classes"
                            v-if="callsToAction.callToActionAtIndex(i) !== null"
                        >
                            <div
                                class="flex h-full items-center justify-center"
                            >
                                <CallToAction
                                    :content="
                                        callsToAction.callToActionAtIndex(i)
                                            ?.content
                                    "
                                    :link="
                                        callsToAction.callToActionAtIndex(i)
                                            ?.link
                                    "
                                    :linkLabel="
                                        callsToAction.callToActionAtIndex(i)
                                            ?.link_label
                                    "
                                />
                            </div>
                        </div>
                    </li>
                </TransitionGroup>
            </ul>
        </Transition>
    </div>
</template>

<style scoped>
    .list-move, /* apply transition to moving elements */
    .list-enter-active,
    .list-leave-active {
        transition: all 0.5s ease;
    }

    .list-enter-from,
    .list-leave-to {
        opacity: 0;
        transform: scale(0.7);
    }

    /* ensure leaving items are taken out of layout flow so that moving animations can be calculated correctly. */
    .list-leave-active {
        position: absolute;
    }
</style>

<style>
    form.project-filters {
        .multiselect {
            @apply border-0 bg-transparent;
        }

        .multiselect-single-label,
        .multiselect-option,
        .multiselect-placeholder {
            @apply px-2;
        }

        .multiselect-caret {
            @apply ml-0 p-0;
            @apply h-[0.4em] w-[0.4em];
            @apply top-[2px];
            @apply rotate-180;
            order: 10;
            mask-image: url('~/assets/icons/arrow-northeast.svg');
        }

        .multiselect:hover .multiselect-caret {
            @apply -translate-x-1/2 translate-y-1/2;
        }
    }

    form + ul > li:last-child {
        margin-bottom: 0;
    }
</style>
